import React, { useState, useEffect } from "react";
import VoucherSection from "./VoucherSection";
import { Product } from "@/components/product-step/types";
import { formatPrice } from "@/components/product-step/types";
import { Edit } from "lucide-react";
import { trackViewCart } from "@/utils/analytics";
import { resolveProductKey } from "@/locales";
import type { BookingLanguage } from "@/types/booking";

interface PriceSummaryProps {
  selectedProductDetails: Product[];
  discount: {
    code: string;
    type: "percentage" | "fixed_amount";
    value: number;
  } | null;
  t: {
    selectedProducts: string;
    totalPrice: string;
  };
  language: BookingLanguage;
  packageComponentMap?: Record<string, string[]>;
  onApplyVoucher?: (discount: {
    code: string;
    type: "percentage" | "fixed_amount";
    value: number;
  }) => void;
  onEditProducts?: () => void;
}

const PriceSummary = ({
  selectedProductDetails,
  discount,
  t,
  language,
  packageComponentMap,
  onApplyVoucher,
  onEditProducts,
}: PriceSummaryProps) => {
  const [localDiscount, setLocalDiscount] = useState(discount);

  // Filter out component products
  const displayProducts = React.useMemo(() => {
    // Get all component IDs that should be hidden
    const allComponentIds = Object.values(packageComponentMap || {}).flat();

    // Return only products that are not components (not in the allComponentIds array)
    return selectedProductDetails.filter(
      (product) => !allComponentIds.includes(product.id),
    );
  }, [selectedProductDetails, packageComponentMap]);

  // Calculate base price (sum of displayed products)
  const basePrice = displayProducts.reduce(
    (sum, product) => sum + (product.default_price?.unit_amount || 0),
    0,
  );

  // Calculate discount amount
  const discountAmount = localDiscount
    ? localDiscount.type === "percentage"
      ? (basePrice * localDiscount.value) / 100
      : localDiscount.value
    : 0;

  // Calculate final price
  const finalPrice = Math.max(0, basePrice - discountAmount);

  // Track view_cart event when price summary is displayed
  useEffect(() => {
    if (displayProducts.length > 0) {
      const cartItems = displayProducts.map((product) => ({
        item_id: product.id,
        item_name:
          product.translations?.name ||
          resolveProductKey(language, product.name),
        item_category: product.category?.name || "Medical Scan",
        price: product.default_price
          ? product.default_price.unit_amount / 100
          : 0,
        currency: product.default_price?.currency || "CHF",
        quantity: 1,
      }));

      trackViewCart(
        cartItems,
        finalPrice / 100,
        cartItems[0]?.currency || "CHF",
      );
    }
  }, [displayProducts, finalPrice]);

  // Handle voucher application
  const handleApplyVoucher = (newDiscount: {
    code: string;
    type: "percentage" | "fixed_amount";
    value: number;
  }) => {
    setLocalDiscount(newDiscount);
    if (onApplyVoucher) {
      onApplyVoucher(newDiscount);
    }
  };

  const handleEditClick = () => {
    if (onEditProducts) {
      onEditProducts();
    }
  };

  return null;
};

export default PriceSummary;
